import { Building2, HandHeart, Hospital, HomeIcon } from "lucide-react"

export function WhoItsFor() {
  const industries = [
    {
      name: "Assisted Living",
      description: "Navigate 144G requirements and senior care regulations with precision and ease.",
      icon: <HandHeart className="h-10 w-10 text-primary dark:text-primary-light" />,
    },
    {
      name: "Home Healthcare",
      description: "Streamline compliance with complex 245D regulations for home-based services.",
      icon: <HomeIcon className="h-10 w-10 text-primary dark:text-primary-light" />,
    },
    {
      name: "Adult Day Services",
      description: "Meet all DHS requirements for adult day programs and community-based care.",
      icon: <Building2 className="h-10 w-10 text-primary dark:text-primary-light" />,
    },
    {
      name: "Care Facilities",
      description: "Ensure your residential care facility meets all state and federal guidelines.",
      icon: <Hospital className="h-10 w-10 text-primary dark:text-primary-light" />,
    },
  ]

  return (
    <section id="who-its-for" className="relative py-20 md:py-28 overflow-hidden bg-slate-50 dark:bg-[#0F172A] scroll-mt-20">
      {/* Decorative elements for visual appeal */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Gradient pattern */}
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/5 dark:bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary-light/5 dark:bg-primary-light/10 rounded-full blur-3xl"></div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-grid-gray-500/[0.02] dark:bg-grid-gray-400/[0.03] bg-[length:30px_30px]"></div>
      </div>

      <div className="container px-4 md:px-8 lg:px-12 mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light mb-4 backdrop-blur-sm">
            WHO IT&apos;S FOR
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground dark:text-white mb-6 tracking-tight">
            Tailored for Minnesota <span className="text-primary dark:text-primary-light">Care Providers</span>
          </h2>
          <p className="text-xl text-muted-foreground dark:text-gray-300 max-w-3xl mx-auto">
            Navigate complex Minnesota-specific compliance requirements with confidence and peace of mind.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {industries.map((industry, index) => (
            <div
              key={index}
              className="relative group"
            >
              {/* Animated glow effect on hover */}
              <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-primary/20 to-primary-light/20 dark:from-primary/40 dark:to-primary-light/40 opacity-0 group-hover:opacity-100 transition-all duration-300 blur-md"></div>
              
              <div className="relative bg-white dark:bg-[#1E293B] rounded-xl shadow-xl border border-gray-100 dark:border-gray-800/50 p-6 transition-all duration-300 group-hover:-translate-y-1 flex flex-col h-full">
                <div className="mb-5 rounded-2xl bg-primary/10 dark:bg-primary-light/20 p-5 w-fit">
                  {industry.icon}
                </div>
                <h3 className="text-2xl font-bold text-foreground dark:text-white mb-3">{industry.name}</h3>
                <p className="text-muted-foreground dark:text-gray-300 flex-grow">{industry.description}</p>
                
                {/* Decorative accent line */}
                <div className="h-1 w-12 bg-gradient-to-r from-primary to-primary-light rounded-full mt-5 transition-all duration-300 group-hover:w-20"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional section highlighting regulatory focus */}
        <div className="mt-20 max-w-4xl mx-auto">
          <div className="relative">
            {/* Subtle glow effect */}
            <div className="absolute -inset-4 rounded-2xl bg-gradient-to-r from-primary/5 to-primary-light/5 dark:from-primary/10 dark:to-primary-light/10 blur-xl"></div>
            
            <div className="relative rounded-xl bg-white dark:bg-[#1E293B] border border-gray-100 dark:border-gray-800/50 p-6 md:p-8 shadow-lg">
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
                <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
                  <svg className="w-10 h-10 text-primary dark:text-primary-light" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8.5 12H15.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 15.5V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground dark:text-white mb-2">Minnesota-Specific Focus</h3>
                  <p className="text-muted-foreground dark:text-gray-300">
                    Our AI is trained on the exact Minnesota statutes, regulations, and requirements that apply to your healthcare business - not generic compliance information.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
} 